import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Save, Upload } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { DEFAULT_CONTENT, IMAGE_LABELS } from "@/data/content";
import { AdminReviews } from "@/components/site/AdminReviews";
import { AdminEnquiries } from "@/components/site/AdminEnquiries";
import { AiWriteButton } from "@/components/site/AiWriteButton";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

const deepCopy = (o) => JSON.parse(JSON.stringify(o));

const inputCls =
  "w-full rounded-sm border border-[#D4AF37]/30 bg-white px-3 py-2 text-sm text-[#1B3A6B] outline-none transition-colors focus:border-[#D4AF37]";

const formatError = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => e?.msg || "")
      .filter(Boolean)
      .join(" ");
  return "Something went wrong. Please try again.";
};

export default function Admin() {
  const [token, setToken] = useState(
    () => localStorage.getItem("tarah_admin_token") || "",
  );
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadContent = async () => {
    const { data } = await axios.get(`${API}/content`);
    setDraft({
      images: { ...deepCopy(DEFAULT_CONTENT.images), ...(data.images || {}) },
      menu:
        Array.isArray(data.menu) && data.menu.length
          ? data.menu
          : deepCopy(DEFAULT_CONTENT.menu),
      gifts:
        Array.isArray(data.gifts) && data.gifts.length
          ? data.gifts
          : deepCopy(DEFAULT_CONTENT.gifts),
      payment: {
        ...deepCopy(DEFAULT_CONTENT.payment),
        ...(data.payment || {}),
      },
    });
  };

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setChecking(false);
        return;
      }
      try {
        await axios.get(`${API}/admin/me`, { headers });
        await loadContent();
      } catch {
        localStorage.removeItem("tarah_admin_token");
        setToken("");
      } finally {
        setChecking(false);
      }
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, {
        email,
        password,
      });
      localStorage.setItem("tarah_admin_token", data.token);
      setToken(data.token);
      await loadContent();
      toast.success("Welcome back, Tarah");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const logout = () => {
    localStorage.removeItem("tarah_admin_token");
    setToken("");
    setDraft(null);
  };

  const update = (path, value) => {
    setDraft((d) => {
      const copy = deepCopy(d);
      const keys = path.split(".");
      let ref = copy;
      keys.slice(0, -1).forEach((k) => {
        ref = ref[k];
      });
      ref[keys[keys.length - 1]] = value;
      return copy;
    });
    setDirty(true);
  };

  const upload = async (key, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, fd, { headers });
      update(`images.${key}`, `${BASE}/api/files/${data.path}`);
      toast.success("Photo uploaded — press Save Changes to publish");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = deepCopy(draft);
      clean.menu.forEach((m) => {
        m.flavors = (m.flavors || []).map((s) => s.trim()).filter(Boolean);
        m.styles = (m.styles || [])
          .map((s) =>
            typeof s === "string"
              ? { name: s.trim() }
              : { ...s, name: s.name.trim() },
          )
          .filter((s) => s.name);
        m.fillings = (m.fillings || []).map((s) => s.trim()).filter(Boolean);
        m.allergens = (m.allergens || []).map((s) => s.trim()).filter(Boolean);
      });
      await axios.put(`${API}/admin/content`, clean, { headers });
      setDraft(clean);
      setDirty(false);
      toast.success("Saved — your website now shows these changes");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FDFBF7]">
        <p className="text-sm text-[#67718A]">Checking session…</p>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FDFBF7] px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-sm border border-[#D4AF37]/30 bg-white p-8 shadow-sm"
        >
          <div className="mb-8 flex justify-center">
            <Logo />
          </div>
          <p className="mb-6 text-center font-label text-xs tracking-[0.3em] text-[#9A7B24]">
            STUDIO MANAGER
          </p>
          <div className="space-y-4">
            <input
              data-testid="admin-email-input"
              type="email"
              placeholder="Email"
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              data-testid="admin-password-input"
              type="password"
              placeholder="Password"
              className={inputCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <button
            data-testid="admin-login-button"
            type="submit"
            className="mt-6 w-full rounded-full bg-[#D4AF37] py-3 text-sm font-semibold text-[#1B3A6B] transition-colors hover:bg-[#8A6D1F] hover:text-white"
          >
            Sign In
          </button>
          <Link
            to="/"
            data-testid="admin-back-home-link"
            className="mt-5 flex items-center justify-center gap-2 text-xs text-[#67718A] hover:text-[#9A7B24]"
          >
            <ArrowLeft size={13} /> Back to website
          </Link>
        </form>
      </div>
    );
  }

  if (!draft) return null;

  return (
    <div
      className="min-h-screen bg-[#FDFBF7] pb-32"
      data-testid="admin-dashboard"
    >
      <header className="sticky top-0 z-40 border-b border-[#D4AF37]/25 bg-[#FDFBF7]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <Logo />
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="text-xs tracking-wide text-[#67718A] hover:text-[#9A7B24]"
            >
              View website
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2 text-xs font-medium text-[#9A7B24] transition-colors hover:bg-[#D4AF37] hover:text-[#1B3A6B]"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 pt-12">
        <h1 className="font-display text-3xl text-[#1B3A6B]">Studio Manager</h1>
        <p className="mt-2 text-sm text-[#67718A]">
          Swap photos, edit prices, flavours, allergens and gift boxes, and
          approve customer reviews. Press{" "}
          <span className="font-semibold text-[#9A7B24]">Save Changes</span> at
          the bottom to publish.
        </p>

        <section className="mt-12" data-testid="admin-photos-section">
          <h2 className="font-display text-2xl text-[#1B3A6B]">Photos</h2>
          <div className="mt-6 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
            {Object.keys(IMAGE_LABELS).map((key) => (
              <div
                key={key}
                data-testid={`photo-card-${key}`}
                className="overflow-hidden rounded-sm border border-[#D4AF37]/25 bg-white"
              >
                <img
                  src={draft.images[key]}
                  alt={IMAGE_LABELS[key]}
                  className="h-32 w-full object-cover"
                />
                <div className="p-3">
                  <p className="text-xs font-medium leading-snug text-[#1B3A6B]">
                    {IMAGE_LABELS[key]}
                  </p>
                  <label
                    data-testid={`photo-upload-${key}`}
                    className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-full border border-[#D4AF37]/50 px-3 py-2 text-xs font-medium text-[#9A7B24] transition-colors hover:bg-[#D4AF37] hover:text-[#1B3A6B]"
                  >
                    <Upload size={13} /> Replace photo
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => upload(key, e.target.files[0])}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16" data-testid="admin-menu-section">
          <h2 className="font-display text-2xl text-[#1B3A6B]">
            Menu — Prices & Flavours
          </h2>
          <div className="mt-6 space-y-6">
            {draft.menu.map((m, i) => (
              <div
                key={m.id}
                data-testid={`menu-editor-${m.id}`}
                className="rounded-sm border border-[#D4AF37]/25 bg-white p-6"
              >
                <p className="font-display text-xl text-[#1B3A6B]">{m.name}</p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-[#67718A]">
                      Starting price
                    </span>
                    <input
                      data-testid={`menu-${m.id}-from-price`}
                      className={inputCls}
                      value={m.fromPrice}
                      onChange={(e) =>
                        update(`menu.${i}.fromPrice`, e.target.value)
                      }
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-[#67718A]">
                      Serves
                    </span>
                    <input
                      data-testid={`menu-${m.id}-serves`}
                      className={inputCls}
                      value={m.serves}
                      onChange={(e) =>
                        update(`menu.${i}.serves`, e.target.value)
                      }
                    />
                  </label>
                </div>
                <label className="mt-4 block">
                  <span className="mb-1 flex items-center justify-between text-xs uppercase tracking-[0.16em] text-[#67718A]">
                    Description
                    <AiWriteButton
                      task="description"
                      testId={`menu-${m.id}-ai-description`}
                      headers={headers}
                      context={`Collection: ${m.name}. Tagline: ${m.tagline}. Prices: ${(m.tiers || []).map((t) => `${t.name} ${t.price}`).join(", ")}. Styles: ${(m.styles || []).map((st) => st.name).join(", ") || "none"}. Flavours: ${(m.flavors || []).join(", ")}. Fillings: ${(m.fillings || []).join(", ")}. Current description: ${m.description}`}
                      onResult={(text) => update(`menu.${i}.description`, text)}
                    />
                  </span>
                  <textarea
                    data-testid={`menu-${m.id}-description`}
                    rows={3}
                    className={`${inputCls} resize-none`}
                    value={m.description}
                    onChange={(e) =>
                      update(`menu.${i}.description`, e.target.value)
                    }
                  />
                </label>
                <label className="mt-4 block">
                  <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-[#67718A]">
                    Price note (optional, shown under the prices)
                  </span>
                  <input
                    data-testid={`menu-${m.id}-price-note`}
                    className={inputCls}
                    value={m.priceNote || ""}
                    onChange={(e) =>
                      update(`menu.${i}.priceNote`, e.target.value)
                    }
                  />
                </label>
                <label className="mt-4 block">
                  <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-[#67718A]">
                    Styles / themes (comma separated, optional — reference
                    photos are in the Photos section above)
                  </span>
                  <input
                    data-testid={`menu-${m.id}-styles`}
                    className={inputCls}
                    value={(m.styles || []).map((st) => st.name).join(", ")}
                    onChange={(e) =>
                      update(
                        `menu.${i}.styles`,
                        e.target.value.split(",").map((name) => ({
                          name,
                          image: (m.styles || []).find(
                            (st) => st.name === name.trim(),
                          )?.image,
                        })),
                      )
                    }
                  />
                </label>
                <label className="mt-4 block">
                  <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-[#67718A]">
                    Flavours (comma separated)
                  </span>
                  <input
                    data-testid={`menu-${m.id}-flavors`}
                    className={inputCls}
                    value={(m.flavors || []).join(", ")}
                    onChange={(e) =>
                      update(`menu.${i}.flavors`, e.target.value.split(","))
                    }
                  />
                </label>
                <label className="mt-4 block">
                  <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-[#67718A]">
                    Fillings (comma separated)
                  </span>
                  <input
                    data-testid={`menu-${m.id}-fillings`}
                    className={inputCls}
                    value={(m.fillings || []).join(", ")}
                    onChange={(e) =>
                      update(`menu.${i}.fillings`, e.target.value.split(","))
                    }
                  />
                </label>
                <label className="mt-4 block">
                  <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-[#67718A]">
                    Allergens (comma separated)
                  </span>
                  <input
                    data-testid={`menu-${m.id}-allergens`}
                    className={inputCls}
                    value={(m.allergens || []).join(", ")}
                    onChange={(e) =>
                      update(`menu.${i}.allergens`, e.target.value.split(","))
                    }
                  />
                </label>
                <div className="mt-4 space-y-3">
                  {m.tiers.map((t, j) => (
                    <div
                      key={j}
                      className="grid gap-3 sm:grid-cols-3"
                      data-testid={`menu-${m.id}-tier-${j}`}
                    >
                      <input
                        className={inputCls}
                        value={t.name}
                        onChange={(e) =>
                          update(`menu.${i}.tiers.${j}.name`, e.target.value)
                        }
                        placeholder="Tier name"
                      />
                      <input
                        className={inputCls}
                        value={t.serves}
                        onChange={(e) =>
                          update(`menu.${i}.tiers.${j}.serves`, e.target.value)
                        }
                        placeholder="Serves"
                      />
                      <input
                        data-testid={`menu-${m.id}-tier-${j}-price`}
                        className={inputCls}
                        value={t.price}
                        onChange={(e) =>
                          update(`menu.${i}.tiers.${j}.price`, e.target.value)
                        }
                        placeholder="Price"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16" data-testid="admin-extras-section">
          <h2 className="font-display text-2xl text-[#1B3A6B]">
            Finishing Touches (extras under the cake menu)
          </h2>
          <div className="mt-6 grid gap-4">
            {(draft.extras || []).map((x, i) => (
              <div
                key={x.id}
                data-testid={`extra-editor-${x.id}`}
                className="grid gap-3 rounded-sm border border-[#D4AF37]/25 bg-white p-4 sm:grid-cols-3"
              >
                <input
                  data-testid={`extra-${x.id}-name`}
                  className={inputCls}
                  value={x.name}
                  onChange={(e) => update(`extras.${i}.name`, e.target.value)}
                  placeholder="Name"
                />
                <input
                  data-testid={`extra-${x.id}-detail`}
                  className={inputCls}
                  value={x.detail || ""}
                  onChange={(e) => update(`extras.${i}.detail`, e.target.value)}
                  placeholder="Short detail"
                />
                <input
                  data-testid={`extra-${x.id}-price`}
                  className={inputCls}
                  value={x.price}
                  onChange={(e) => update(`extras.${i}.price`, e.target.value)}
                  placeholder="Price"
                />
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16" data-testid="admin-payment-section">
          <h2 className="font-display text-2xl text-[#1B3A6B]">
            Deposit Payments (Revolut)
          </h2>
          <p className="mt-2 text-sm text-[#67718A]">
            Paste your Revolut.me link (Revolut app → profile → Revolut.me →
            copy link). The "Pay deposit via Revolut" button appears on the
            website and in customer emails only once a link is saved.
          </p>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-[#67718A]">
                Revolut.me link
              </span>
              <input
                data-testid="payment-revolut-link"
                className={inputCls}
                placeholder="https://revolut.me/yourname"
                value={draft.payment?.revolutLink || ""}
                onChange={(e) =>
                  update("payment.revolutLink", e.target.value.trim())
                }
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-[#67718A]">
                Note shown beside the button
              </span>
              <input
                data-testid="payment-deposit-note"
                className={inputCls}
                value={draft.payment?.depositNote || ""}
                onChange={(e) => update("payment.depositNote", e.target.value)}
              />
            </label>
          </div>
        </section>

        <section className="mt-16" data-testid="admin-gifts-section">
          <h2 className="font-display text-2xl text-[#1B3A6B]">Gift Boxes</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            {draft.gifts.map((g, i) => (
              <div
                key={g.id}
                data-testid={`gift-editor-${g.id}`}
                className="rounded-sm border border-[#D4AF37]/25 bg-white p-6"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-[#67718A]">
                      Name
                    </span>
                    <input
                      data-testid={`gift-${g.id}-name`}
                      className={inputCls}
                      value={g.name}
                      onChange={(e) =>
                        update(`gifts.${i}.name`, e.target.value)
                      }
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-[#67718A]">
                      Price
                    </span>
                    <input
                      data-testid={`gift-${g.id}-price`}
                      className={inputCls}
                      value={g.price}
                      onChange={(e) =>
                        update(`gifts.${i}.price`, e.target.value)
                      }
                    />
                  </label>
                </div>
                <label className="mt-4 block">
                  <span className="mb-1 block text-xs uppercase tracking-[0.16em] text-[#67718A]">
                    Description
                  </span>
                  <textarea
                    data-testid={`gift-${g.id}-description`}
                    rows={3}
                    className={`${inputCls} resize-none`}
                    value={g.description}
                    onChange={(e) =>
                      update(`gifts.${i}.description`, e.target.value)
                    }
                  />
                </label>
              </div>
            ))}
          </div>
        </section>

        <AdminEnquiries headers={headers} />
        <AdminReviews headers={headers} />
      </main>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#D4AF37]/25 bg-white/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <p
            className="text-xs text-[#67718A]"
            data-testid="admin-dirty-indicator"
          >
            {dirty ? "You have unpublished changes" : "All changes published"}
          </p>
          <button
            data-testid="admin-save-button"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 rounded-full bg-[#D4AF37] px-8 py-3 text-sm font-semibold text-[#1B3A6B] transition-colors hover:bg-[#8A6D1F] hover:text-white disabled:opacity-60"
          >
            <Save size={15} /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
